import { match } from 'ts-pattern';
import { implementCustomExpressions } from '../__generated/implementation.js';
import { AnchorPointPosition, BendDirection, BendOrientation, StrikePlateShapeType, StrikeTonguePosition } from '../__generated/model/index.js';
import { bendData } from '../data/bend-data.js';
import { round } from '../util/round.js';
export const customBendExpressions = implementCustomExpressions({
    CustomBend: {
        $middlePoint: (self)=>{
            const segments = self.$plate.$segmentInfo;
            if (segments.start) {
                const startSegment = segments.start;
                const middleX = match([
                    self.orientation,
                    self.direction
                ]).with([
                    BendOrientation.Vertical,
                    BendDirection.Front
                ], ()=>(startSegment.end.x ?? 0) + startSegment.bend.innerRadius + startSegment.plate.thickness).with([
                    BendOrientation.Vertical,
                    BendDirection.Back
                ], ()=>(startSegment.end.x ?? 0) + startSegment.bend.innerRadius).otherwise(()=>self.$plate.$sizeX / 2);
                const middleY = match([
                    self.orientation,
                    self.direction
                ]).with([
                    BendOrientation.Horizontal,
                    BendDirection.Front
                ], ()=>(startSegment.end.y ?? 0) + startSegment.bend.innerRadius + startSegment.plate.thickness).with([
                    BendOrientation.Horizontal,
                    BendDirection.Back
                ], ()=>(startSegment.end.y ?? 0) + startSegment.bend.innerRadius).otherwise(()=>self.$plate.$sizeY / 2);
                return {
                    x: round(middleX, 2),
                    y: round(middleY, 2)
                };
            }
            return {
                x: round(self.$plate.$sizeX / 2, 2),
                y: round(self.$plate.$sizeY / 2, 2)
            };
        },
        $minFlangeLength: (s)=>{
            const thickness = s.$plate.thickness;
            const bendTool = bendData.d90.getTool(thickness);
            if (!bendTool) {
                return 0;
            }
            return bendTool.minFlangeLength;
        },
        $startLengthMin: (s)=>{
            const bendTool = bendData.d90.getTool(s.$plate.thickness);
            if (!bendTool) {
                return s.$minFlangeLength;
            }
            return Math.max(s.$minFlangeLength, bendTool.innerRadius + s.$plate.$cornerRadius);
        },
        $startLengthMax: (s)=>{
            const startSegmentInfo = s.$plate.$segmentInfo.start;
            if (!startSegmentInfo) {
                return Math.min(s.$plate.$maxHorizontalLength, s.$plate.$maxVerticalLength);
            }
            return s.orientation === BendOrientation.Horizontal ? s.$plate.$maxHorizontalLength - (s.$plate.$sizeY - startSegmentInfo.end.y) : s.$plate.$maxVerticalLength - (s.$plate.$sizeX - startSegmentInfo.end.x);
        },
        $endLengthMin: (s)=>{
            const bendTool = bendData.d90.getTool(s.$plate.thickness);
            if (!bendTool) {
                return s.$minFlangeLength;
            }
            return Math.max(s.$minFlangeLength, bendTool.innerRadius + s.$plate.$cornerRadius);
        },
        $endLengthMax: (s)=>{
            const endSegmentInfo = s.$plate.$segmentInfo.end;
            if (!endSegmentInfo) {
                return Math.min(s.$plate.$maxHorizontalLength, s.$plate.$maxVerticalLength);
            }
            return s.orientation === BendOrientation.Horizontal ? s.$plate.$maxHorizontalLength - (s.$plate.$sizeY - endSegmentInfo.start.y) : s.$plate.$maxVerticalLength - (s.$plate.$sizeX - endSegmentInfo.start.x);
        },
        $safeZone: (s)=>{
            const bendTool = bendData.d90.getTool(s.$plate.thickness);
            if (!bendTool) {
                return 0;
            }
            return bendTool.innerRadius * 3;
        },
        $startIndicatorEndX: (self)=>self.orientation === BendOrientation.Horizontal ? 0 : self.$middlePoint.x,
        $startIndicatorEndY: (self)=>self.orientation === BendOrientation.Horizontal ? self.$middlePoint.y : 0,
        $startIndicatorStartZ: (self)=>self.$plate.thickness,
        $startIndicatorEndZ: (self)=>self.$plate.thickness,
        $endIndicatorStartX: (self)=>self.orientation === BendOrientation.Horizontal ? 0 : self.$middlePoint.x,
        $endIndicatorEndX: (self)=>self.$endIndicatorStartX,
        $endIndicatorStartY: (self)=>self.orientation === BendOrientation.Horizontal ? self.$middlePoint.y : 0,
        $endIndicatorEndY: (self)=>self.$endIndicatorStartY,
        $endIndicatorStartZ: (self)=>self.$plate.thickness,
        $endIndicatorEndZ: (self)=>self.$endIndicatorStartZ + (self.direction === BendDirection.Front ? -self.endInnerLength - self.$plate.thickness : self.endInnerLength),
        $sizeIndicatorExtrusionX: (self)=>self.orientation === BendOrientation.Horizontal ? self.$plate.$sizeIndicatorExtrusion : 0,
        $sizeIndicatorExtrusionY: (self)=>self.orientation === BendOrientation.Horizontal ? 0 : self.$plate.$sizeIndicatorExtrusion,
        $indicatorLabelFontSize: (self)=>self.$plate.$indicatorLabelFontSize
    },
    StrikeBend: {
        $absCoords: ()=>({
                x: 0,
                y: 0
            }),
        // $anchorPoints: self => self.$plate.$allAnchorPoints.filter(ap => ap.shapeId === self.id),
        $anchorableShapesChain: ()=>[],
        $boundingBox: (self)=>{
            const segmentInfo = self.$plate.$segmentInfo;
            const bendSegment = segmentInfo.bend;
            if (!bendSegment) {
                return {
                    bottomLeft: {
                        x: 0,
                        y: 0
                    },
                    topRight: {
                        x: 0,
                        y: 0
                    }
                };
            }
            return {
                bottomLeft: {
                    x: Math.min(bendSegment.start.x, bendSegment.end.x),
                    y: Math.min(bendSegment.start.y, bendSegment.end.y)
                },
                topRight: {
                    x: Math.max(bendSegment.start.x, bendSegment.end.x),
                    y: Math.max(bendSegment.start.y, bendSegment.end.y)
                }
            };
        },
        $center: (self)=>({
                x: (self.$boundingBox.topRight.x + self.$boundingBox.bottomLeft.x) / 2,
                y: (self.$boundingBox.topRight.y + self.$boundingBox.bottomLeft.y) / 2
            }),
        $padding: ()=>({
                top: 0,
                right: 0,
                bottom: 0,
                left: 0
            }),
        $middlePoint: (self)=>self.$center,
        $hiddenAnchorPoints: (self)=>{
            if (self.$plate.shapeType === StrikePlateShapeType.TStrike) {
                return [
                    AnchorPointPosition.top,
                    AnchorPointPosition.bottom,
                    AnchorPointPosition.center
                ];
            } else {
                return [
                    AnchorPointPosition.top,
                    AnchorPointPosition.bottom,
                    AnchorPointPosition.center,
                    ...self.$plate.tonguePosition === StrikeTonguePosition.left ? [
                        AnchorPointPosition.topRight,
                        AnchorPointPosition.bottomRight,
                        AnchorPointPosition.right
                    ] : [
                        AnchorPointPosition.topLeft,
                        AnchorPointPosition.bottomLeft,
                        AnchorPointPosition.left
                    ]
                ];
            }
        }
    }
});

//# sourceMappingURL=bends.js.map